/**
 * AssetRadar — typed HTTP layer.
 *
 * Everything the SPA sends to FastAPI goes through `apiFetch`. It
 *   - always sends the session cookie (`credentials: 'same-origin'`; the SPA is
 *     served from the same origin as the API, in dev via the Vite proxy),
 *   - serialises JSON bodies and sets the content type (but never for FormData,
 *     where the browser must add the multipart boundary itself),
 *   - returns `undefined` for 204 responses instead of blowing up on res.json(),
 *   - throws `ApiError` carrying the *German* server message verbatim, and
 *   - reports every 401 to the handler registered by AuthProvider.
 *
 * The wrappers below are the only place endpoint paths are written down; page
 * components import them and never call fetch directly.
 */

import type {
  AdminRolesResponse,
  AdminSessionsResponse,
  AdminUsersResponse,
  AssetRecord,
  AssetsResponse,
  AuditLogResponse,
  AuthUser,
  BewertungRequest,
  BewertungResponse,
  ChangePasswordRequest,
  CmdbCardRow,
  CmdbCountsResponse,
  CmdbDeleteResponse,
  CmdbListResponse,
  CmdbMetaResponse,
  CmdbOptionsResponse,
  CraCheckInstanceResponse,
  CraCheckOverviewResponse,
  CreateCsafSourceRequest,
  CreateUserRequest,
  CsafSourceResponse,
  CsafSourcesResponse,
  CsafSourceVulnerabilitiesResponse,
  CsafSourceTestRequest,
  CsafSourceTestResponse,
  CsafSyncResponse,
  CsafUploadResponse,
  DashboardOverviewResponse,
  DependencyTrackSettingsUpdate,
  DependencyTrackSettingsUpdateResponse,
  DemoBackup,
  DemoJob,
  DtCvesResponse,
  DtFindingsResponse,
  DtInstancesResponse,
  DtSbomResponse,
  EmailDraftsResponse,
  ExploitabilityResponse,
  ExportProduktinstanzenResponse,
  GraphResponse,
  HealthResponse,
  ImportApplyRequest,
  ImportApplyResponse,
  ImportPlanResponse,
  LoginRequest,
  LoginResponse,
  NotifyRequest,
  NotifyResponse,
  PsirtOverviewResponse,
  RescanResponse,
  SchemaCleanupResponse,
  SchemaExportResponse,
  SchemaProvisionResponse,
  SchemaUploadResponse,
  SchemaWipeResponse,
  SettingsResponse,
  TriageAdvisory,
  TriageAttachment,
  TriageDocuments,
  UpdateCsafSourceRequest,
  UpdateUserRequest,
  UserResponse,
  ValidationErrorItem,
  VersendetRequest,
  VersendetResponse,
  VulnerabilityEventsResponse,
  XDomeSchemaResponse,
  XDomeUploadResponse,
} from './types';

export function fetchChangeStatus(): Promise<{
  revision: number;
  pending: boolean;
  attempts: number;
  failed: boolean;
  poll_failed: boolean;
  poll_interval_seconds: number;
}> {
  return apiFetch('/api/changes');
}

/* ------------------------------------------------------------------------- *
 * Errors
 * ------------------------------------------------------------------------- */

/**
 * A non-2xx response. `detail` is always a display-ready string: FastAPI's
 * `{detail: "…"}` verbatim, the joined messages of a pydantic 422
 * `{detail: [{loc,msg,type}]}`, or a generic `HTTP <status>` fallback.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly detail: string;
  /** The parsed response body, when there was one — for callers that need more. */
  readonly body: unknown;

  constructor(status: number, detail: string, body: unknown) {
    super(detail);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
    this.body = body;
  }
}

/** True for the "the network/server is unreachable" case (no HTTP status). */
export function isNetworkError(error: unknown): boolean {
  return error instanceof TypeError;
}

/** Best-effort German message for anything that can be thrown by this module. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.detail;
  if (isNetworkError(error)) return 'Server nicht erreichbar.';
  if (error instanceof Error && error.message) return error.message;
  return 'Unbekannter Fehler.';
}

function isValidationErrorItem(value: unknown): value is ValidationErrorItem {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { msg?: unknown }).msg === 'string'
  );
}

/** Normalise both FastAPI error shapes plus junk bodies into one string. */
function detailFromBody(body: unknown, status: number): string {
  const fallback = `HTTP ${status}`;
  if (typeof body === 'string' && body.trim() !== '') return body;
  if (typeof body !== 'object' || body === null) return fallback;

  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === 'string' && detail.trim() !== '') return detail;
  if (Array.isArray(detail)) {
    const msgs = detail.map((item) => {
      if (!isValidationErrorItem(item)) return String(item);
      const loc = Array.isArray(item.loc) ? item.loc.filter((p) => p !== 'body').join('.') : '';
      return loc ? `${loc}: ${item.msg}` : item.msg;
    });
    const joined = msgs.filter(Boolean).join(' · ');
    if (joined) return joined;
  }
  return fallback;
}

/* ------------------------------------------------------------------------- *
 * 401 hook
 * ------------------------------------------------------------------------- */

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/**
 * Register the callback invoked on EVERY 401 (except the initial
 * `GET /api/auth/me` probe, which passes `skipUnauthorizedHandler`).
 * AuthProvider uses it to drop the user so the router renders the login screen.
 * Returns an unsubscribe function.
 */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): () => void {
  unauthorizedHandler = handler;
  return () => {
    if (unauthorizedHandler === handler) unauthorizedHandler = null;
  };
}

/* ------------------------------------------------------------------------- *
 * Core fetch
 * ------------------------------------------------------------------------- */

export interface ApiFetchInit extends Omit<RequestInit, 'body'> {
  /** Plain JSON body (serialised) or a FormData/Blob passed through as-is. */
  body?: unknown;
  /**
   * Suppress the global 401 callback. Used for the auth probe, where a 401 is
   * the normal "not logged in" answer and not a session loss.
   */
  skipUnauthorizedHandler?: boolean;
}

const JSON_CT = 'application/json';

function isRawBody(body: unknown): body is BodyInit {
  return (
    body instanceof FormData ||
    body instanceof Blob ||
    body instanceof URLSearchParams ||
    body instanceof ArrayBuffer ||
    typeof body === 'string'
  );
}

/**
 * Perform a request against the AssetRadar API.
 *
 * @typeParam T - the expected response shape. `apiFetch<void>` is the right
 *   choice for 204 endpoints; the resolved value is then `undefined`.
 * @throws {@link ApiError} on any non-2xx response.
 */
export async function apiFetch<T>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const { body, skipUnauthorizedHandler, headers, ...rest } = init;
  const finalHeaders = new Headers(headers);
  finalHeaders.set('Accept', JSON_CT);

  let finalBody: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    if (isRawBody(body)) {
      finalBody = body;
    } else {
      finalBody = JSON.stringify(body);
      if (!finalHeaders.has('Content-Type')) finalHeaders.set('Content-Type', JSON_CT);
    }
  }

  const res = await fetch(path, {
    // SameSite=Strict + same-origin is the CSRF defence; no token needed.
    credentials: 'same-origin',
    ...rest,
    headers: finalHeaders,
    ...(finalBody === undefined ? {} : { body: finalBody }),
  });

  const parsed = await parseBody(res);

  if (!res.ok) {
    if (res.status === 401 && !skipUnauthorizedHandler) unauthorizedHandler?.();
    throw new ApiError(res.status, detailFromBody(parsed, res.status), parsed);
  }

  return parsed as T;
}

export function fetchTriageDocuments(eventId: number): Promise<TriageDocuments> {
  return apiFetch(`/api/dashboard/vulnerability-events/${eventId}/documents`);
}

export function saveTriageAdvisory(
  eventId: number,
  body: Pick<TriageAdvisory, 'content' | 'template_name' | 'revision'>,
): Promise<TriageAdvisory> {
  return apiFetch(`/api/dashboard/vulnerability-events/${eventId}/advisory`, { method: 'PUT', body });
}

export function uploadTriageAttachment(eventId: number, file: File): Promise<TriageAttachment> {
  const body = new FormData();
  body.append('file', file);
  return apiFetch(`/api/dashboard/vulnerability-events/${eventId}/attachments`, { method: 'POST', body });
}

export function deleteTriageAttachment(eventId: number, attachmentId: string): Promise<void> {
  return apiFetch(triageAttachmentUrl(eventId, attachmentId), { method: 'DELETE' });
}

export function triageAttachmentUrl(eventId: number, attachmentId: string): string {
  return `/api/dashboard/vulnerability-events/${eventId}/attachments/${encodeURIComponent(attachmentId)}`;
}

async function parseBody(res: Response): Promise<unknown> {
  if (res.status === 204 || res.status === 205 || res.status === 304) return undefined;
  const text = await res.text();
  if (text === '') return undefined;
  const contentType = res.headers.get('Content-Type') ?? '';
  if (contentType.includes('json')) {
    try {
      return JSON.parse(text) as unknown;
    } catch {
      return text;
    }
  }
  return text;
}

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

/* ------------------------------------------------------------------------- *
 * Authentication — /api/auth/*
 * ------------------------------------------------------------------------- */

/** `GET /api/auth/me`. A 401 here is the normal unauthenticated case. */
export function fetchCurrentUser(): Promise<AuthUser> {
  return apiFetch<AuthUser>('/api/auth/me', { skipUnauthorizedHandler: true });
}

/** `POST /api/auth/login` — 401 on bad credentials, 429 while locked out. */
export function login(body: LoginRequest): Promise<LoginResponse> {
  return apiFetch<LoginResponse>('/api/auth/login', {
    method: 'POST',
    body,
    skipUnauthorizedHandler: true,
  });
}

/** `POST /api/auth/logout` — 204, clears the cookie. */
export function logout(): Promise<void> {
  return apiFetch<void>('/api/auth/logout', {
    method: 'POST',
    skipUnauthorizedHandler: true,
  });
}

/** `POST /api/auth/password` — self-service change, 204 on success. */
export function changePassword(body: ChangePasswordRequest): Promise<void> {
  return apiFetch<void>('/api/auth/password', { method: 'POST', body });
}

/* ------------------------------------------------------------------------- *
 * Dashboard — /api/dashboard/*
 * ------------------------------------------------------------------------- */

/** `GET /api/dashboard/overview` — `only_affected` is the only server filter. */
export function fetchDashboardOverview(onlyAffected: boolean): Promise<DashboardOverviewResponse> {
  return apiFetch<DashboardOverviewResponse>(
    `/api/dashboard/overview${qs({ only_affected: onlyAffected })}`,
  );
}

/**
 * `POST /api/dashboard/notify` — records Benachrichtigung cards for the
 * selected ProduktInstanzen and drafts one mail per Kunde. Nothing is really
 * sent (MVP).
 */
export function notifyProdukte(body: NotifyRequest): Promise<NotifyResponse> {
  return apiFetch<NotifyResponse>('/api/dashboard/notify', { method: 'POST', body });
}

/* ------------------------------------------------------------------------- *
 * Triage — vulnerability events
 * ------------------------------------------------------------------------- */

/**
 * `GET /api/dashboard/vulnerability-events`.
 * Service failures propagate so the UI can distinguish them from an empty queue.
 */
export function fetchVulnerabilityEvents(): Promise<VulnerabilityEventsResponse> {
  return apiFetch<VulnerabilityEventsResponse>('/api/dashboard/vulnerability-events');
}

/** `GET /api/audit-log` — reconstructed chronicle of verdicts, notifications and evidence. */
export function fetchAuditLog(): Promise<AuditLogResponse> {
  return apiFetch<AuditLogResponse>('/api/audit-log');
}

/** Keep dashboard access available without triage.read; surface service failures. */
export async function fetchVulnerabilityEventsSoft(): Promise<VulnerabilityEventsResponse> {
  try {
    return await fetchVulnerabilityEvents();
  } catch (error) {
    if (error instanceof ApiError && error.status === 403) {
      return { count: 0, offen: 0, events: [] };
    }
    throw error;
  }
}

/** `POST /api/dashboard/vulnerability-events/{id}/bewertung` — records a verdict. */
export function submitBewertung(
  eventId: number,
  body: BewertungRequest,
): Promise<BewertungResponse> {
  return apiFetch<BewertungResponse>(
    `/api/dashboard/vulnerability-events/${eventId}/bewertung`,
    { method: 'POST', body },
  );
}

/**
 * `POST /api/dashboard/vulnerability-events/{id}/email` — MUTATING: it re-runs
 * the Benachrichtigung fan-out before drafting. 409 unless the verdict is
 * 'BETROFFEN' (show the German detail verbatim).
 */
export function fetchEmailDrafts(eventId: number): Promise<EmailDraftsResponse> {
  return apiFetch<EmailDraftsResponse>(
    `/api/dashboard/vulnerability-events/${eventId}/email`,
    { method: 'POST' },
  );
}

/** `POST /api/dashboard/benachrichtigungen/{id}/versendet` — a body IS required. */
export function markBenachrichtigungVersendet(
  benachrichtigungId: number,
  body: VersendetRequest = {},
): Promise<VersendetResponse> {
  return apiFetch<VersendetResponse>(
    `/api/dashboard/benachrichtigungen/${benachrichtigungId}/versendet`,
    { method: 'POST', body },
  );
}

/** `GET /cve/{cve_id}/exploitability` — KEV/EPSS decision support. */
export function fetchExploitability(
  cveId: string,
  source?: string,
): Promise<ExploitabilityResponse> {
  return apiFetch<ExploitabilityResponse>(
    `/cve/${encodeURIComponent(cveId)}/exploitability${qs({ source })}`,
  );
}

/* ------------------------------------------------------------------------- *
 * Stammdaten — /api/cmdb/*
 *
 * The generic CRUD surface over the CRA classes in CMDBuild. `klass` is always
 * a class name from `CmdbMetaResponse.classes` — never user input.
 * ------------------------------------------------------------------------- */

/** `GET /api/cmdb/meta` — class + attribute metadata and the live lookup values. */
export function fetchCmdbMeta(): Promise<CmdbMetaResponse> {
  return apiFetch<CmdbMetaResponse>('/api/cmdb/meta');
}

/**
 * `GET /api/cmdb/counts` — card count per class. A count of `-1` means the class
 * could not be read (schema not provisioned yet), not "empty".
 */
export function fetchCmdbCounts(): Promise<CmdbCountsResponse> {
  return apiFetch<CmdbCountsResponse>('/api/cmdb/counts');
}

/**
 * `GET /api/cmdb/options` — reference-picker options for SEVERAL classes in one
 * round trip. Pass every target class of the form being opened.
 */
export function fetchCmdbOptions(classes: readonly string[]): Promise<CmdbOptionsResponse> {
  return apiFetch<CmdbOptionsResponse>(`/api/cmdb/options${qs({ classes: classes.join(',') })}`);
}

/**
 * `GET /api/cmdb/graph` — the whole master data as nodes and edges.
 *
 * `collapse` folds the Stückliste join class into direct edges; pass `false`
 * to see the Stücklisteneinträge as nodes of their own.
 */
export function fetchCmdbGraph(collapse = true): Promise<GraphResponse> {
  return apiFetch<GraphResponse>(`/api/cmdb/graph${qs({ collapse: collapse ? undefined : 'false' })}`);
}

/** `GET /api/cmdb/{klass}/cards` — `q` filters server-side over label + search attrs. */
export function fetchCmdbCards(klass: string, q?: string): Promise<CmdbListResponse> {
  return apiFetch<CmdbListResponse>(
    `/api/cmdb/${encodeURIComponent(klass)}/cards${qs({ q: q?.trim() || undefined })}`,
  );
}

/** `GET /api/cmdb/{klass}/cards/{id}` — one card plus what references it. */
export function fetchCmdbCard(klass: string, cardId: number): Promise<CmdbCardRow> {
  return apiFetch<CmdbCardRow>(`/api/cmdb/${encodeURIComponent(klass)}/cards/${cardId}`);
}

/**
 * `POST /api/cmdb/{klass}/cards` — 422 on a validation error, 409 on a duplicate
 * natural key or a class that is generated rather than hand-created. The German
 * detail is display-ready.
 */
export function createCmdbCard(
  klass: string,
  values: Record<string, unknown>,
): Promise<CmdbCardRow> {
  return apiFetch<CmdbCardRow>(`/api/cmdb/${encodeURIComponent(klass)}/cards`, {
    method: 'POST',
    body: values,
  });
}

/** `PUT /api/cmdb/{klass}/cards/{id}`. Only the submitted attributes are written. */
export function updateCmdbCard(
  klass: string,
  cardId: number,
  values: Record<string, unknown>,
): Promise<CmdbCardRow> {
  return apiFetch<CmdbCardRow>(`/api/cmdb/${encodeURIComponent(klass)}/cards/${cardId}`, {
    method: 'PUT',
    body: values,
  });
}

/**
 * `DELETE /api/cmdb/{klass}/cards/{id}` — 409 while other cards reference it.
 * `cascade` additionally removes purely dependent cards (Stücklisteneinträge,
 * Abweichungen, Benachrichtigungen) and never touches a class with its own
 * identity, so a Kunde with instances still refuses.
 */
export function deleteCmdbCard(
  klass: string,
  cardId: number,
  cascade = false,
): Promise<CmdbDeleteResponse> {
  return apiFetch<CmdbDeleteResponse>(
    `/api/cmdb/${encodeURIComponent(klass)}/cards/${cardId}${qs({ cascade })}`,
    { method: 'DELETE' },
  );
}

/* ------------------------------------------------------------------------- *
 * Dependency-Track mirror — /api/dt/*  (read-only)
 * ------------------------------------------------------------------------- */

/** `GET /api/dt/instanzen` — every instance with its DT project state. */
export function fetchDtInstances(): Promise<DtInstancesResponse> {
  return apiFetch<DtInstancesResponse>('/api/dt/instanzen');
}

/** `GET /api/dt/instanzen/{id}/findings` — all severities, worst first. */
export function fetchDtFindings(
  instanzId: number,
  includeSuppressed = false,
): Promise<DtFindingsResponse> {
  return apiFetch<DtFindingsResponse>(
    `/api/dt/instanzen/${instanzId}/findings${qs({ include_suppressed: includeSuppressed })}`,
  );
}

/** `GET /api/dt/cves` — every CVE with the products it affects. */
export function fetchDtCves(includeSuppressed = false): Promise<DtCvesResponse> {
  return apiFetch<DtCvesResponse>(
    `/api/dt/cves${qs({ include_suppressed: includeSuppressed })}`,
  );
}

/** `GET /api/dt/instanzen/{id}/sbom` — DT components + the CMDB configuration. */
export function fetchDtSbom(instanzId: number): Promise<DtSbomResponse> {
  return apiFetch<DtSbomResponse>(`/api/dt/instanzen/${instanzId}/sbom`);
}

/* ------------------------------------------------------------------------- *
 * CRA-Check — /api/cra-check/*
 * ------------------------------------------------------------------------- */

/**
 * `GET /api/psirt/overview` — PSIRT teams and one row per product, evaluated
 * against the CRA requirements. Writes use the generic Stammdaten CRUD on
 * `PsirtTeam` / `ProduktVerantwortung`.
 */
export function fetchPsirtOverview(): Promise<PsirtOverviewResponse> {
  return apiFetch<PsirtOverviewResponse>('/api/psirt/overview');
}

/** `GET /api/cra-check/overview` — the traffic light for every ProduktInstanz. */
export function fetchCraCheckOverview(): Promise<CraCheckOverviewResponse> {
  return apiFetch<CraCheckOverviewResponse>('/api/cra-check/overview');
}

/** `GET /api/cra-check/instanzen/{id}` — the traffic light of one instance. */
export function fetchCraCheckInstance(instanzId: number): Promise<CraCheckInstanceResponse> {
  return apiFetch<CraCheckInstanceResponse>(`/api/cra-check/instanzen/${instanzId}`);
}

/* ------------------------------------------------------------------------- *
 * CSV import — /api/import/*
 * ------------------------------------------------------------------------- */

/**
 * `POST /api/import/plan` — multipart, field name exactly 'file'.
 * Writes nothing; produces a server-side plan valid for 30 minutes.
 */
export function planImport(file: File): Promise<ImportPlanResponse> {
  const form = new FormData();
  form.append('file', file);
  return apiFetch<ImportPlanResponse>('/api/import/plan', { method: 'POST', body: form });
}

/**
 * `POST /api/import/apply`. Always transmit the FULL decision map for every
 * non-'unchanged' entry — a missing entry_id defaults to 'incoming'
 * server-side. 404 means the plan TTL expired and the CSV must be re-uploaded.
 */
export function applyImport(body: ImportApplyRequest): Promise<ImportApplyResponse> {
  return apiFetch<ImportApplyResponse>('/api/import/apply', { method: 'POST', body });
}

/** `POST /upload/xdome` — multipart, field name 'file'. */
export function uploadXDomeCsv(file: File): Promise<XDomeUploadResponse> {
  const form = new FormData();
  form.append('file', file);
  return apiFetch<XDomeUploadResponse>('/upload/xdome', { method: 'POST', body: form });
}

/* ------------------------------------------------------------------------- *
 * Administration — /api/admin/*
 * ------------------------------------------------------------------------- */

export function fetchAdminUsers(): Promise<AdminUsersResponse> {
  return apiFetch<AdminUsersResponse>('/api/admin/users');
}

/** 409 on a duplicate e-mail, 422 on a rejected password. */
export function createAdminUser(body: CreateUserRequest): Promise<UserResponse> {
  return apiFetch<UserResponse>('/api/admin/users', { method: 'POST', body });
}

/** Send only what changed. 409 when the last active admin would be lost. */
export function updateAdminUser(userId: string, body: UpdateUserRequest): Promise<UserResponse> {
  return apiFetch<UserResponse>(`/api/admin/users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    body,
  });
}

/** 204 on success, 409 for the last active admin. */
export function deleteAdminUser(userId: string): Promise<void> {
  return apiFetch<void>(`/api/admin/users/${encodeURIComponent(userId)}`, { method: 'DELETE' });
}

/** The authoritative role → permission matrix. */
export function fetchAdminRoles(): Promise<AdminRolesResponse> {
  return apiFetch<AdminRolesResponse>('/api/admin/roles');
}

/** Active session metadata for the audit view — never contains token hashes. */
export function fetchAdminSessions(): Promise<AdminSessionsResponse> {
  return apiFetch<AdminSessionsResponse>('/api/admin/sessions');
}

/* ------------------------------------------------------------------------- *
 * System / operations
 * ------------------------------------------------------------------------- */

/** `GET /health` — public. */
export function fetchHealth(): Promise<HealthResponse> {
  return apiFetch<HealthResponse>('/health', { skipUnauthorizedHandler: true });
}

/** `GET /settings` — secret-free echo of the effective configuration. */
export function fetchSettings(): Promise<SettingsResponse> {
  return apiFetch<SettingsResponse>('/settings');
}

/** Persist and immediately activate the narrow, write-only DT configuration. */
export function updateDependencyTrackSettings(
  body: DependencyTrackSettingsUpdate,
): Promise<DependencyTrackSettingsUpdateResponse> {
  return apiFetch<DependencyTrackSettingsUpdateResponse>('/settings/dependency-track', {
    method: 'PATCH',
    body,
  });
}

export function fetchAssets(): Promise<AssetsResponse> {
  return apiFetch<AssetsResponse>('/assets');
}

export function fetchAsset(assetId: string): Promise<AssetRecord> {
  return apiFetch<AssetRecord>(`/assets/${encodeURIComponent(assetId)}`);
}

/** `POST /rescan/{asset_id}` — re-uploads a stored SBOM to Dependency-Track. */
export function rescanAsset(assetId: string): Promise<RescanResponse> {
  return apiFetch<RescanResponse>(`/rescan/${encodeURIComponent(assetId)}`, { method: 'POST' });
}

/** `POST /scan/csaf` — every enabled CSAF source → DT internal vulnerabilities. */
export function runCsafSync(): Promise<CsafSyncResponse> {
  return apiFetch<CsafSyncResponse>('/scan/csaf', { method: 'POST' });
}

/* ------------------------------------------------------------------------- *
 * CSAF advisory sources — /api/csaf/*
 *
 * The catalogue of vendor feeds the sync pulls from. Reading it needs
 * `system.admin`, every write `scan.run` — a source decides what the nightly
 * job downloads and writes into Dependency-Track.
 * ------------------------------------------------------------------------- */

/** `GET /api/csaf/sources` — the catalogue plus the server's kind vocabulary. */
export function fetchCsafSources(): Promise<CsafSourcesResponse> {
  return apiFetch<CsafSourcesResponse>('/api/csaf/sources');
}

/** Read the source's actual DT inventory, including unchanged CVEs. */
export function fetchCsafSourceVulnerabilities(
  sourceId: string,
  page: number,
  pageSize: number,
  search: string,
  signal?: AbortSignal,
): Promise<CsafSourceVulnerabilitiesResponse> {
  const query = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (search) query.set('search', search);
  return apiFetch<CsafSourceVulnerabilitiesResponse>(
    `/api/csaf/sources/${encodeURIComponent(sourceId)}/vulnerabilities?${query}`,
    { signal },
  );
}

/** `POST /api/csaf/sources` — 409 on a duplicate URL, 422 on an invalid one. */
export function createCsafSource(body: CreateCsafSourceRequest): Promise<CsafSourceResponse> {
  return apiFetch<CsafSourceResponse>('/api/csaf/sources', { method: 'POST', body });
}

/**
 * `POST /api/csaf/sources/test` — fetch and parse an unsaved source without
 * persisting catalogue state or writing anything to Dependency-Track.
 */
export function testCsafSource(body: CsafSourceTestRequest): Promise<CsafSourceTestResponse> {
  return apiFetch<CsafSourceTestResponse>('/api/csaf/sources/test', { method: 'POST', body });
}

/** `PATCH /api/csaf/sources/{id}` — send only what changed. */
export function updateCsafSource(
  sourceId: string,
  body: UpdateCsafSourceRequest,
): Promise<CsafSourceResponse> {
  return apiFetch<CsafSourceResponse>(`/api/csaf/sources/${encodeURIComponent(sourceId)}`, {
    method: 'PATCH',
    body,
  });
}

/** `DELETE /api/csaf/sources/{id}` — cached advisory documents stay on disk. */
export function deleteCsafSource(sourceId: string): Promise<CsafSourceResponse> {
  return apiFetch<CsafSourceResponse>(`/api/csaf/sources/${encodeURIComponent(sourceId)}`, {
    method: 'DELETE',
  });
}

/**
 * `POST /api/csaf/sources/{id}/documents` — multipart, field name exactly
 * 'file'. Stores the CSAF document under the source AND ingests it right away,
 * so the response is a full sync result for that one advisory. 422 when the
 * file is not a CSAF 2.0 document.
 */
export function uploadCsafDocument(sourceId: string, file: File): Promise<CsafUploadResponse> {
  const form = new FormData();
  form.append('file', file);
  return apiFetch<CsafUploadResponse>(
    `/api/csaf/sources/${encodeURIComponent(sourceId)}/documents`,
    { method: 'POST', body: form },
  );
}

/**
 * `POST /api/csaf/sync` — sync the named sources, or every enabled one when
 * `sourceIds` is omitted. A source named explicitly is synced even when it is
 * disabled.
 */
export function syncCsafSources(sourceIds?: readonly string[]): Promise<CsafSyncResponse> {
  return apiFetch<CsafSyncResponse>('/api/csaf/sync', {
    method: 'POST',
    body: { source_ids: sourceIds ?? null },
  });
}

/** `POST /export/produktinstanzen` — per-instance SBOM export to DT. */
export interface XDomeSbomResponse {
  filename: string;
  sbom: string;
  device_count: number;
  os_count: number;
}

export function generateXdomeSbom(file: File, specVersion: string, demoData = false): Promise<XDomeSbomResponse> {
  const body = new FormData();
  body.append('file', file);
  body.append('spec_version', specVersion);
  body.append('demo_data', String(demoData));
  return apiFetch<XDomeSbomResponse>('/api/sbom/xdome', { method: 'POST', body });
}

export function exportProduktinstanzen(): Promise<ExportProduktinstanzenResponse> {
  return apiFetch<ExportProduktinstanzenResponse>('/export/produktinstanzen', { method: 'POST' });
}

/** `POST /schema/cra-watch` — idempotent data-model provisioning. */
export function provisionCraWatchSchema(): Promise<SchemaProvisionResponse> {
  return apiFetch<SchemaProvisionResponse>('/schema/cra-watch', { method: 'POST' });
}

/**
 * `POST /schema/cra-watch/upload` — provision a schema file (YAML/JSON).
 *
 * `dryRun` (the default) writes nothing and reports what would be created;
 * a 400 carries every validation problem of the file in one message.
 */
export function uploadSchemaFile(file: File, dryRun: boolean): Promise<SchemaUploadResponse> {
  const form = new FormData();
  form.append('file', file);
  form.append('dry_run', dryRun ? 'true' : 'false');
  return apiFetch<SchemaUploadResponse>('/schema/cra-watch/upload', {
    method: 'POST',
    body: form,
  });
}

/** `GET /schema/cra-watch/export` — current model as an editable YAML document. */
export function exportSchemaFile(): Promise<SchemaExportResponse> {
  return apiFetch<SchemaExportResponse>('/schema/cra-watch/export');
}

/**
 * `POST /schema/cra-watch/wipe` — DESTRUCTIVE: deletes every AssetRadar card.
 * Callers must gate this behind an explicit German confirmation.
 */
export function wipeCraWatchData(): Promise<SchemaWipeResponse> {
  return apiFetch<SchemaWipeResponse>('/schema/cra-watch/wipe', { method: 'POST' });
}

/**
 * Demo backups — only exist for APP_VARIANT=demo with the explicit server-side
 * gate enabled (404 otherwise).
 */
export function fetchDemoBackups(signal?: AbortSignal): Promise<{ backups: DemoBackup[] }> {
  return apiFetch('/api/demo/backups', { signal, cache: 'no-store' });
}

/** Start a backup whose progress survives leaving or reloading the page. */
export function startDemoBackup(label: string, signal?: AbortSignal): Promise<DemoJob> {
  return apiFetch<DemoJob>('/api/demo/backups', { method: 'POST', body: { label }, signal });
}

/**
 * Start restoring a backup. The confirmation is deliberately fixed on both
 * sides; the System page adds the human confirmation before this can be called.
 */
export function startDemoRestore(backupId: string, signal?: AbortSignal): Promise<DemoJob> {
  return apiFetch<DemoJob>(`/api/demo/backups/${encodeURIComponent(backupId)}/restore`, {
    method: 'POST',
    body: { confirmation: 'DEMO RESET' },
    signal,
  });
}

export function fetchCurrentDemoJob(signal?: AbortSignal): Promise<{ job: DemoJob | null }> {
  return apiFetch('/api/demo/jobs/current', { signal, cache: 'no-store' });
}

export function fetchDemoJob(jobId: string, signal?: AbortSignal): Promise<DemoJob> {
  return apiFetch(`/api/demo/jobs/${encodeURIComponent(jobId)}`, { signal, cache: 'no-store' });
}

/** `POST /schema/cra-watch/cleanup-deprecated` — destructive schema surgery. */
export function cleanupDeprecatedSchema(): Promise<SchemaCleanupResponse> {
  return apiFetch<SchemaCleanupResponse>('/schema/cra-watch/cleanup-deprecated', {
    method: 'POST',
  });
}

/** `POST /schema/xdome` — xDome attributes/domains. */
export function provisionXDomeSchema(): Promise<XDomeSchemaResponse> {
  return apiFetch<XDomeSchemaResponse>('/schema/xdome', { method: 'POST' });
}
